<template>
  <div>
    <p class="outcome-hint">
      Each job belongs to one originating lead. Estimated and contracted values
      are separate from invoices, credits, payments and refunds. All amounts
      below are for this job only.
    </p>
    <div v-for="job in outcome.jobs" :key="job.id" class="outcome-job">
      <strong
        >Job #{{ job.id }} · {{ job.currency }} · {{ job.work_state }}</strong
      >
      <p>
        Estimated {{ money(job.estimated_minor) }} · Contracted
        {{ money(job.contracted_minor) }}<br />Invoiced, net
        {{ money(job.invoiced_minor) }} · Collected, net
        {{ money(job.collected_minor) }}
      </p>
      <button type="button" class="outcome-secondary" @click="edit(job)">
        Edit job / add financial entry
      </button>
      <ul>
        <li v-for="entry in job.entries" :key="entry.id">
          #{{ entry.id }} · {{ entry.kind }} · {{ money(entry.amount_minor)
          }}<span v-if="entry.parent_id">
            · references #{{ entry.parent_id }}</span
          >
        </li>
      </ul>
    </div>
    <button type="button" class="outcome-secondary" @click="newJob">
      Add a job
    </button>
    <form v-if="draft" @submit.prevent="save" class="outcome-job-form">
      <h4>
        {{ selected ? 'Edit job #' + selected.id : 'New attributable job' }}
      </h4>
      <div class="outcome-grid">
        <label
          >Currency<select v-model="draft.currency" :disabled="!!selected">
            <option v-for="c in currencies" :key="c">{{ c }}</option>
          </select></label
        >
        <label
          >Work status<select v-model="draft.work_state">
            <option v-for="s in workStates" :key="s" :value="s">
              {{ s.replace('_', ' ') }}
            </option>
          </select></label
        >
      </div>
      <div class="outcome-grid">
        <label
          >Estimated value (optional)<input
            v-model="draft.estimated"
            type="number"
            min="0"
            step="0.01"
            inputmode="decimal"
        /></label>
        <label
          >Contracted value (optional)<input
            v-model="draft.contracted"
            type="number"
            min="0"
            step="0.01"
            inputmode="decimal"
        /></label>
      </div>
      <label
        >Financial entry<select v-model="draft.entryKind">
          <option value="">No new entry</option>
          <option value="invoice">Invoice</option>
          <option value="credit">Credit against an invoice</option>
          <option value="payment">Payment received</option>
          <option value="refund">Refund of a payment</option>
        </select></label
      >
      <label v-if="draft.entryKind"
        >Entry amount<input
          v-model="draft.entryAmount"
          type="number"
          min="0"
          step="0.01"
          inputmode="decimal"
          required
      /></label>
      <label v-if="['credit', 'refund'].includes(draft.entryKind)"
        >Original entry<select v-model="draft.parentId" required>
          <option :value="null" disabled>Select an invoice or payment</option>
          <option
            v-for="entry in parentEntries"
            :key="entry.id"
            :value="entry.id"
          >
            #{{ entry.id }} · {{ entry.kind }} · {{ money(entry.amount_minor) }}
          </option>
        </select></label
      >
      <label
        >Reason or note (optional)<input
          v-model="draft.reason"
          maxlength="2000"
      /></label>
      <div class="outcome-actions">
        <button :disabled="busy">{{ busy ? 'Saving…' : 'Save job' }}</button
        ><button
          type="button"
          class="outcome-secondary"
          :disabled="busy"
          @click="draft = null"
        >
          Cancel
        </button>
      </div>
      <p v-if="error" class="outcome-error" role="alert">{{ error }}</p>
      <p class="outcome-hint">
        Blank means unknown. Zero means a known zero. Entries are individual
        transactions; do not enter cumulative payment totals.
      </p>
    </form>
  </div>
</template>
<script>
import axios from 'axios'
export default {
  name: 'OutcomeJobs',
  props: {
    outcome: { type: Object, required: true },
    leadId: { type: [Number, String], required: true },
  },
  data() {
    return {
      draft: null,
      selected: null,
      busy: false,
      error: '',
      currencies: ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'NZD'],
      workStates: [
        'unknown',
        'scheduled',
        'in_progress',
        'completed',
        'cancelled',
      ],
    }
  },
  computed: {
    parentEntries() {
      return this.selected
        ? this.selected.entries.filter(
            (e) =>
              e.kind ===
              (this.draft.entryKind === 'refund' ? 'payment' : 'invoice')
          )
        : []
    },
  },
  methods: {
    uuid() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16
        return (c === 'x' ? r : (r & 3) | 8).toString(16)
      })
    },
    money(minor) {
      return minor === null ? 'Unknown' : (Number(minor) / 100).toFixed(2)
    },
    amount(value) {
      if (value === '' || value === null) return null
      if (!/^\d+(\.\d{1,2})?$/.test(String(value)))
        throw new Error('Use a positive amount with up to two decimal places.')
      const parts = String(value).split('.')
      return Number(parts[0]) * 100 + Number((parts[1] || '').padEnd(2, '0'))
    },
    newJob() {
      this.edit(null)
    },
    edit(job) {
      this.selected = job
      this.error = ''
      this.draft = {
        source_key: job ? job.source_key : this.uuid(),
        currency: job ? job.currency : 'USD',
        work_state: job ? job.work_state : 'unknown',
        estimated:
          job && job.estimated_minor !== null
            ? (Number(job.estimated_minor) / 100).toFixed(2)
            : '',
        contracted:
          job && job.contracted_minor !== null
            ? (Number(job.contracted_minor) / 100).toFixed(2)
            : '',
        entryKind: '',
        entryAmount: '',
        entryKey: this.uuid(),
        parentId: null,
        reason: '',
      }
    },
    async save() {
      this.busy = true
      this.error = ''
      try {
        const job = {
          source_key: this.draft.source_key,
          currency: this.draft.currency,
          work_state: this.draft.work_state,
          estimated_minor: this.amount(this.draft.estimated),
          contracted_minor: this.amount(this.draft.contracted),
        }
        if (this.draft.entryKind) {
          job.entry = {
            entry_key: this.draft.entryKey,
            kind: this.draft.entryKind,
            amount_minor: this.amount(this.draft.entryAmount),
          }
          if (['credit', 'refund'].includes(this.draft.entryKind))
            job.entry.parent_id = this.draft.parentId
        }
        const { data } = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/call-tracking/outcomes/${this.leadId}`,
          {
            version: this.outcome.version,
            request_id: this.uuid(),
            reason: this.draft.reason || null,
            job,
          },
          { headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt } }
        )
        this.draft = null
        this.$emit('saved', data)
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Could not save this job.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>
<style scoped>
.outcome-job {
  padding: 14px 0;
  border-bottom: 1px solid #e3ebed;
  margin-bottom: 12px;
}
.outcome-job-form {
  background: #f5f8f8;
  border-radius: 10px;
  padding: 14px;
  margin-top: 14px;
}
.outcome-job-form h4 {
  margin: 0;
}
.outcome-job ul {
  padding-left: 18px;
  font-size: 12px;
}
</style>
